/**
 * Glance view — one division's month as four numbers and three pictures (2026-10-26).
 *
 * Callum + Caitlyn: the market screens are "massive information overload". This is the front page of BI Markets
 * and Sales Market insights: the division's numbers, a picture of the markets it is gaining and losing, a picture
 * of where to look next, and the companies to approach as cards — news folded to three headlines. The detail stays
 * one click away (the host app's own tabs, and the one-page PDF).
 *
 *     renderGlanceView(el, g, { labels, onDetail, pdfUrl })
 *
 * `g` is `isg_common.market_intel.glance.build()` as JSON — the same figures as the PDF and the monthly email,
 * so the three can never disagree. EVERY visible word is in `labels` (the app speaks, ui-kit draws — the
 * table-tools rule), with `{name}` placeholders. The component carries its own `gv-` styles, injected once.
 */
import { formatCurrency } from '../utils/formatters.js';
import { getLocale } from '../i18n/index.js';
import { escapeHtml as esc } from '../utils/helpers.js';

const count = n => (n == null ? '—' : new Intl.NumberFormat(getLocale()).format(n));
const pct = v => `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(0)}%`;
const fill = (s, v = {}) => String(s || '').replace(/\{(\w+)\}/g, (_m, k) => (v[k] == null ? '' : v[k]));

function money(v, cur) {
  if (v == null) return '—';
  return formatCurrency(v, cur, { notation: 'compact', maximumFractionDigits: 1 });
}

function css() {
  if (document.getElementById('gv-styles')) return;
  const s = document.createElement('style');
  s.id = 'gv-styles';
  s.textContent = `
.gv { display: grid; gap: var(--space-4); }
.gv-kpis { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: var(--space-3); }
.gv-kpi { background: var(--surface-primary); border: 1px solid var(--border-primary); border-top: 4px solid var(--gv-c, var(--color-primary, #006699)); border-radius: var(--radius-md); padding: var(--space-3) var(--space-4); }
.gv-kpi-l { font-size: var(--text-xs); text-transform: uppercase; letter-spacing: .06em; color: var(--text-secondary); }
.gv-kpi-v { font-size: 2rem; font-weight: 300; line-height: 1.15; color: var(--gv-c, var(--color-primary, #006699)); white-space: nowrap; }
.gv-kpi-s { font-size: var(--text-xs); color: var(--text-secondary); }
.gv-up { color: var(--color-success, #15803d); font-weight: 700; } .gv-down { color: var(--color-danger, #bd1b44); font-weight: 700; }
.gv-pics { display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr); gap: var(--space-4); }
.gv-card { background: var(--surface-primary); border: 1px solid var(--border-primary); border-radius: var(--radius-md); padding: var(--space-4); }
.gv-card h3 { margin: 0 0 var(--space-3); font-size: var(--text-base); display: flex; justify-content: space-between; gap: var(--space-2); align-items: baseline; }
.gv-card h3 small { font-weight: 400; font-size: var(--text-xs); color: var(--text-secondary); }
.gv-db { display: grid; grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr) auto; gap: var(--space-3); align-items: center; min-height: 30px; font-size: var(--text-sm); }
.gv-db-track { position: relative; height: 14px; }
.gv-db-track::before { content: ''; position: absolute; left: 0; right: 0; top: 6px; height: 2px; background: var(--surface-secondary); }
.gv-db-zero { position: absolute; top: 0; bottom: 0; width: 1px; background: var(--border-primary); }
.gv-db-seg { position: absolute; top: 5px; height: 4px; border-radius: 2px; opacity: .45; }
.gv-db-seg.gv-g { background: var(--color-success, #15803d); } .gv-db-seg.gv-r { background: var(--color-danger, #bd1b44); }
.gv-db-dot { position: absolute; top: 1px; width: 12px; height: 12px; margin-left: -6px; border-radius: 50%; display: inline-block; }
.gv-db-dot.gv-g { background: var(--color-success, #15803d); } .gv-db-dot.gv-r { background: var(--color-danger, #bd1b44); }
.gv-db-mkt { background: var(--surface-primary); border: 2px solid var(--text-secondary); box-sizing: border-box; }
.gv-db-v { font-size: var(--text-xs); font-weight: 700; white-space: nowrap; text-align: right; }
.gv-db-sec { font-size: var(--text-xs); text-transform: uppercase; letter-spacing: .06em; margin: var(--space-2) 0 2px; }
.gv-db-legend { display: flex; gap: var(--space-4); font-size: var(--text-xs); color: var(--text-secondary); margin-bottom: var(--space-1); }
.gv-db-legend span { display: inline-flex; align-items: center; gap: 6px; }
.gv-db-legend .gv-db-dot { position: static; margin: 0; }
.gv-look { display: grid; gap: var(--space-3); }
.gv-look-t { display: flex; justify-content: space-between; gap: var(--space-2); font-size: var(--text-sm); }
.gv-track { position: relative; height: 12px; background: var(--surface-secondary); border-radius: 6px; overflow: hidden; margin-top: 4px; }
.gv-track > div { position: absolute; left: 0; top: 0; bottom: 0; border-radius: 6px; background: var(--color-primary, #006699); min-width: 3px; }
.gv-look-s { font-size: var(--text-xs); color: var(--text-secondary); margin-top: 2px; }
.gv-tag { display: inline-block; font-size: 10px; text-transform: uppercase; letter-spacing: .05em; padding: 0 5px; border-radius: 3px; color: #fff; margin-right: 6px; background: var(--color-warning, #b45309); }
.gv-tag.gv-grow { background: var(--color-success, #15803d); }
.gv-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(180px, 100%), 1fr)); gap: var(--space-3); }
.gv-co { border: 1px solid var(--border-primary); border-radius: var(--radius-md); padding: var(--space-3); background: var(--surface-secondary); min-width: 0; }
.gv-co-n { font-weight: 600; font-size: var(--text-sm); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.gv-co-v { font-size: 1.35rem; font-weight: 300; color: var(--color-primary, #006699); margin: 2px 0; }
.gv-co-s { font-size: var(--text-xs); color: var(--text-secondary); overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
.gv-news { display: grid; gap: var(--space-2); margin: 0; padding: 0; list-style: none; }
.gv-news a { font-weight: 600; font-size: var(--text-sm); color: var(--text-primary); }
.gv-news span { display: block; font-size: var(--text-xs); color: var(--text-secondary); }
.gv-foot { display: flex; gap: var(--space-2); flex-wrap: wrap; }
.gv-empty { color: var(--text-secondary); font-size: var(--text-sm); margin: 0; }
@media (max-width: 900px) { .gv-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); } .gv-pics { grid-template-columns: minmax(0, 1fr); } }
`;
  document.head.appendChild(s);
}

function kpis(g, L, cur) {
  const h = g.headline || {};
  const tile = (label, value, sub, colour) => `<div class="gv-kpi" style="--gv-c:${colour}"><div class="gv-kpi-l">${esc(label)}</div>
      <div class="gv-kpi-v">${value}</div><div class="gv-kpi-s">${sub}</div></div>`;
  const g12 = h.growth_pct;
  const arrow = g12 == null ? '' : `<span class="${g12 >= 0 ? 'gv-up' : 'gv-down'}">${g12 >= 0 ? '▲' : '▼'} ${Math.abs(g12).toFixed(0)}%</span>`;
  return `<div class="gv-kpis">
    ${tile(L.sales, esc(money(h.sales, cur)), `${arrow} ${esc(fill(L.was, { value: money(h.sales_prev, cur) }))}`, 'var(--color-primary, #006699)')}
    ${tile(L.buying, esc(count(h.buying)), esc(L.buying_sub), 'var(--text-secondary, #63707c)')}
    ${tile(L.new, `+${esc(count(h.new_count))}`, esc(fill(L.new_sub, { value: money(h.new_value, cur) })), 'var(--color-success, #15803d)')}
    ${tile(L.lost, `−${esc(count(h.lapsed_count))}`, esc(fill(L.lost_sub, { value: money(h.lapsed_value, cur) })), 'var(--color-danger, #bd1b44)')}
  </div>`;
}

// A share-judged market is placed by the two-year growth rate the share test used; a market judged on OUR sales
// alone (no market size) by the last 12 months against the 12 before — the change that sorted it gaining/losing.
function ourGrowth(m) {
  if (m.basis !== 'our sales' && m.ours_growth_pct != null) return Number(m.ours_growth_pct);
  return m.ours_prev ? 100 * ((m.ours || 0) / m.ours_prev - 1) : null;
}

/** Each market as two dots on one "% a year" axis: us (coloured) and the market (hollow), joined — green when we
 *  grow faster, red when slower. A market with no size (motorsport, small ones) has only our dot. Gaining rows
 *  first, then losing, so the picture reads top to bottom. */
function dumbbell(g, L, cur) {
  const rows = [
    ...(g.gaining || []).map(m => ({ m, up: true })),
    ...(g.losing || []).map(m => ({ m, up: false })),
  ].map(r => ({ ...r, ours: ourGrowth(r.m), market: r.m.basis === 'our sales' ? null : r.m.market_growth_pct }))
    .filter(r => r.ours != null);
  if (!rows.length) return `<p class="gv-empty">${esc(L.no_markets)}</p>`;
  const vals = rows.flatMap(r => [r.ours, r.market]).filter(v => v != null);
  const lo = Math.max(-100, Math.min(-10, ...vals)), hi = Math.min(200, Math.max(10, ...vals));
  const x = v => `${(100 * (Math.min(hi, Math.max(lo, v)) - lo) / (hi - lo)).toFixed(1)}%`;
  const label = m => (m.industry === 'motorsport'
    ? `<b>${esc(m.country_name || m.country || '')}</b>`
    : `<b>${esc(m.label || '')}</b> <span>${esc(m.country_name || '')}</span>`);
  let section = null;
  const body = rows.map(({ m, up, ours, market }) => {
    const head = section === up ? '' : `<div class="gv-db-sec ${up ? 'gv-up' : 'gv-down'}">${esc(up ? L.gaining_head : L.losing_head)}</div>`;
    section = up;
    const cls = up ? 'gv-g' : 'gv-r';
    const a = market == null ? ours : Math.min(ours, market), b = market == null ? ours : Math.max(ours, market);
    const value = market == null ? fill(L.ours_only, { ours: pct(ours), change: money((m.ours || 0) - (m.ours_prev || 0), cur) })
      : fill(L.vs, { ours: pct(ours), market: pct(market) });
    return `${head}<div class="gv-db" title="${esc(m.sentence || '')}">
      <div class="gv-lab">${label(m)}</div>
      <div class="gv-db-track"><i class="gv-db-zero" style="left:${x(0)}"></i>
        <i class="gv-db-seg ${cls}" style="left:${x(a)};width:calc(${x(b)} - ${x(a)})"></i>
        ${market == null ? '' : `<i class="gv-db-dot gv-db-mkt" style="left:${x(market)}"></i>`}
        <i class="gv-db-dot ${cls}" style="left:${x(ours)}"></i></div>
      <div class="gv-db-v ${up ? 'gv-up' : 'gv-down'}">${esc(value)}</div></div>`;
  }).join('');
  return `<div class="gv-db-legend"><span><i class="gv-db-dot gv-g"></i>${esc(L.legend_us)}</span>
      <span><i class="gv-db-dot gv-db-mkt"></i>${esc(L.legend_market)}</span></div>${body}`;
}

function lookAt(g, L, cur) {
  const rows = g.look_at || [];
  if (!rows.length) return `<p class="gv-empty">${esc(L.no_look)}</p>`;
  const top = Math.max(1, ...rows.map(r => r.market_size || 0));
  return `<div class="gv-look">${rows.map((m) => {
    if (m.kind === 'grid') {
      const pct = m.teams ? Math.round(100 * m.buys / m.teams) : 0;
      return `<div><div class="gv-look-t"><b>${esc(m.label)}</b><span>${esc(fill(L.teams, { buys: m.buys, teams: m.teams }))}</span></div>
        <div class="gv-track"><div style="width:${pct}%"></div></div>
        <div class="gv-look-s">${esc(fill(L.grid_open, { open: m.teams - m.buys }))}</div></div>`;
    }
    const share = m.market_size ? Math.min(100, 100 * (m.ours || 0) / m.market_size) : 0;
    const width = Math.max(8, Math.round(100 * (m.market_size || 0) / top));
    return `<div><div class="gv-look-t"><span><span class="gv-tag ${m.kind === 'grow' ? 'gv-grow' : ''}">${esc(m.kind === 'grow' ? L.grow : L.untapped)}</span><b>${esc(m.label)}</b> <span class="gv-lab"><span>${esc(m.country_name || '')}</span></span></span>
        <b>${esc(money(m.market_size, cur))}</b></div>
      <div class="gv-track" style="width:${width}%"><div style="width:${share.toFixed(1)}%"></div></div>
      <div class="gv-look-s">${esc(fill(L.look_sub, { ours: money(m.ours, cur), growth: m.market_growth_pct == null ? '—' : `${m.market_growth_pct > 0 ? '+' : ''}${Number(m.market_growth_pct).toFixed(0)}%` }))}</div></div>`;
  }).join('')}</div>`;
}

function approach(g, L, cur) {
  const rows = g.approach || [];
  if (!rows.length) return `<p class="gv-empty">${esc(L.no_approach)}</p>`;
  return `<div class="gv-cards">${rows.map((a) => {
    if (a.kind === 'team') {
      return `<div class="gv-co"><div class="gv-co-n" title="${esc(a.name)}">${esc(a.name)}</div>
        <div class="gv-co-v" style="font-size:var(--text-base)">${esc(a.series)}</div>
        <div class="gv-co-s">${esc(L.team_status[a.status] || a.status)}</div></div>`;
    }
    const v = a.top_value ? money(a.top_value, a.top_currency || cur) : L.value_unknown;
    const title = a.top_url ? `<a href="${esc(a.top_url)}" target="_blank" rel="noopener">${esc(a.top_title || '')}</a>` : esc(a.top_title || '');
    return `<div class="gv-co"><div class="gv-co-n" title="${esc(a.name)}">${esc(a.name)}</div>
      <div class="gv-co-v">${esc(v)}</div><div class="gv-co-s">${title}</div></div>`;
  }).join('')}</div>`;
}

function news(g, L) {
  const rows = (g.news || []).slice(0, 3);
  if (!rows.length) return `<p class="gv-empty">${esc(L.no_news)}</p>`;
  return `<ul class="gv-news">${rows.map(n => `<li><a href="${esc(n.url)}" target="_blank" rel="noopener">${esc(n.title)}</a>
    <span>${esc((n.accounts || []).slice(0, 2).join(', '))}</span></li>`).join('')}</ul>`;
}

/** Draw the glance view into `el`. `opts.labels` holds every word; `onDetail` / `pdfUrl` add the two buttons. */
export function renderGlanceView(el, g, opts = {}) {
  css();
  const L = opts.labels || {};
  const cur = g?.meta?.currency;
  el.innerHTML = `<div class="gv">
    ${kpis(g, L, cur)}
    <div class="gv-pics">
      <div class="gv-card"><h3>${esc(L.markets)} <small>${esc(L.markets_sub)}</small></h3>${dumbbell(g, L, cur)}</div>
      <div class="gv-card"><h3>${esc(L.look)} <small>${esc(L.look_hint)}</small></h3>${lookAt(g, L, cur)}</div>
    </div>
    <div class="gv-card"><h3>${esc(L.approach)} <small>${esc((g.approach || [])[0]?.kind === 'team' ? L.approach_teams : L.approach_sub)}</small></h3>${approach(g, L, cur)}</div>
    <div class="gv-card"><h3>${esc(L.news)} <small>${esc(L.news_sub)}</small></h3>${news(g, L)}</div>
    <div class="gv-foot">
      ${opts.onDetail ? `<button class="btn btn-secondary btn-sm" data-gv-detail>${esc(L.detail)}</button>` : ''}
      ${opts.pdfUrl ? `<a class="btn btn-ghost btn-sm" href="${esc(opts.pdfUrl)}">${esc(L.pdf)}</a>` : ''}
    </div>
  </div>`;
  if (opts.onDetail) el.querySelector('[data-gv-detail]').addEventListener('click', opts.onDetail);
}
